<script>
   /**********************************************************************/
   /* PlotSeries                                                         */
   /* --------------------                                               */
   /* shows line-scatter-bar series with selection and color grouping    */
   /* possibility                                                        */
   /**********************************************************************/

   import { getContext } from 'svelte';
   import { val2p } from 'svelte-plots-basic/utils';
   import { LINE_COLORS, LABELS_COLOR, LABELS_COLOR_SELECTED, MAX_LABEL_SYMBOLS } from './Defaults.js';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let xValues;                             // vector or array with x-values
   export let yValues;                             // vector or array with y-values
   export let seriesTitle;                         // title of the series (e.g. 'scores')
   export let plotType;                            // type of plot ('p', 'l', 'h', 'b')
   export let labels;                              // array with labels for plot elements
   export let showLabels;                          // show or not labels for plot elements
   export let lineWidth = 1.0;                     // line height
   export let pch = '●';                           // point character (marker)
   export let colors = undefined;                  // color for every point or bar
   export let mainColor = LINE_COLORS['cal'];      // main color if color grouping is not used
   export let faceColor = 'none';                  // face color for the points
   export let selected = -1;                       // selected data point

   // outputs
   export let minYVal;                             // smallest y-value
   export let maxYVal;                             // largest y-value
   export let minXVal;                             // smallest x-value
   export let maxXVal;                             // largest x-value


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // get axes context
   const axes = getContext('axes');
   const tX = axes.tX;
   const tY = axes.tY;
   const isOk = axes.isOk;
   const scale = axes.scale;

   // local copy plot type
   let plotTypeLocal = '';


   // reactive statement to track changes in "plotType" and "xValues" to define "x" values and
   // recompute x-limits
   let x;
   $: if (plotType && xValues) {
      plotTypeLocal = plotType;
      const xv = xValues;

      if (x !== xv) {
         let _minXVal = Number.POSITIVE_INFINITY;
         let _maxXVal = Number.NEGATIVE_INFINITY;
         for (let i = 0; i < xv.length; i++) {
            _minXVal = Math.min(_minXVal, xv.v[i]);
            _maxXVal = Math.max(_maxXVal, xv.v[i]);
         }
         x = xv;
         minXVal = _minXVal;
         maxXVal = _maxXVal;
      }
   }

   // reactive statement to track changes in "yValues" to define "y" values and
   // recompute y-limits
   let y;
   $: if (yValues) {
      const yv = yValues;

      if (y !== yv) {
         let _minYVal = Number.POSITIVE_INFINITY;
         let _maxYVal = Number.NEGATIVE_INFINITY;
         for (let i = 0; i < yv.length; i++) {
            _minYVal = Math.min(_minYVal, yv.v[i]);
            _maxYVal = Math.max(_maxYVal, yv.v[i]);
         }
         y = yv;
         minYVal = _minYVal;
         maxYVal = _maxYVal;
      }
   }

   // reactive calculations for bar plot parameters
   let bw, bhw, p0, dy;
   $: if ($isOk) {
      const m = axes.LABELS_MARGIN[$scale];
      p0 = axes.transform([0.0], $tY.coords)[0];
      bw = axes.transform([0.8], $tX.objects)[0];
      bhw = bw / 2.0;
      dy = m;
   } else {
      p0 = 0;
      bw = 0;
      bhw = 0;
      dy = 0;
   }

   // reactive calculations for coordinates
   let px, py, lp;
   $: if ($isOk && x && y) {
      if (plotType === 'l' || plotType === 'b') {
         lp = val2p(x, y, $tX, $tY, axes);
      }
      px = axes.transform(x, $tX.coords);
      py = axes.transform(y, $tY.coords);
   } else {
      px = null;
      py = null;
      bw = null;
      lp = null;
   }

   // truncate the labels
   $: labelsShort = labels && Array.isArray(labels) ? labels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null;

   // reactive expression for colors
   $: pc = x && colors && colors.length === x.length ? colors : Array(x.length).fill(mainColor);

   // styles for points, bars, labels, etc.
   const ptStyle = `font-size:1em;fill:${faceColor};text-anchor:middle;cursor:default;stroke-width:2px;user-select:none;`;
   const lbStyle = `font-size:0.85em;fill:${LABELS_COLOR};text-anchor:middle;cursor:default;stroke:${LABELS_COLOR};stroke-width:0.1px;user-select:none;`;
   const slStyle = `font-size:1.2em;fill:none;text-anchor:middle;cursor:default;stroke-width:3px;user-select:none;`;
   const lineStyleStr = `fill:transparent;stroke-width: ${lineWidth}px;`;
   const barsStyleStr = `stroke-width:${lineWidth}px;`;
</script>

{#if $isOk && px && py}


   {#if plotType === 'l' || plotType === 'b'}
      <!-- line plot -->
      <g class="series series-line" title="{seriesTitle}-lines" style={lineStyleStr}>
         <polyline class="line" points={lp} stroke={mainColor}/>
      </g>
   {/if}

   {#if plotType === 'h'}
      <!-- bar plot -->
      <g class="series series-bar" title="{seriesTitle}-bars" style={barsStyleStr}>
      {#each px as v, i}
      <rect x={px[i] - bhw} y={(y.v[i] < 0) ? p0 : py[i]} fill={pc[i]} width={bw} height={y.v[i] < 0 ? py[i] - p0 : p0 - py[i]} />
      {/each}
      </g>
   {/if}

   {#if plotType === 'p' || plotType === 'b'}
      <!-- scatter/points plot -->
      <g class="series series-points" title="{seriesTitle}-points" style={ptStyle} >
      {#each px as v, i}
      <text x={px[i]} y={py[i]} fill={faceColor} stroke={pc[i]}>{pch}</text>
      {/each}
      </g>
   {/if}

   {#if showLabels === 'show' && labelsShort}
      <!-- labels plot -->
      <g class="series series-text" style={lbStyle}>
      {#each px as v, i}
      <text x={px[i]} y={py[i]} dy={y.v[i] < 0 ? dy : -dy}>{labelsShort[i]}</text>
      {/each}
      </g>
   {/if}

   {#if selected >= 0}
      <rect x={0} y={0} width={'100%'} height={'100%'} fill="#ffffffa0" />
      {#if plotType !== 'h'}
      <g class="series series-points" title="{seriesTitle}-points" style={slStyle} dominant-baseline="middle" text-anchor="middle">
      <text x={px[selected]} y={py[selected]} stroke={pc[selected]} >{pch}</text>
      </g>
      {:else}
      <g class="series series-bar" title="{seriesTitle}-bars" style={barsStyleStr}>
      <rect stroke={pc[selected]} fill={pc[selected]} x={px[selected] - bhw} y={(y.v[selected] < 0) ? p0 : py[selected]} width={bw} height={y.v[selected] < 0 ? py[selected] - p0 : p0 - py[selected]} />
      </g>
      {/if}

      {#if showLabels === 'show' && labelsShort && labelsShort.length > 0}
      <g class="series series-text" style={lbStyle}>
      <text stroke={LABELS_COLOR_SELECTED} fill={LABELS_COLOR_SELECTED} x={px[selected]} y={py[selected]} dy={y.v[selected] < 0 ? dy : -dy}>{labelsShort[selected]}</text>
      </g>
      {/if}
   {/if}
{/if}